import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Dices,
  Shield,
  FileText,
  Activity,
  Mail,
  Eye,
  TrendingUp,
  Gift,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  Megaphone,
  Inbox,
} from "lucide-react";
import { Logo } from "../Logo";
import styles from "./AdminSidebar.module.css";

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  external?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: "Dashboard",
    items: [
      { path: "/espace-admin", label: "Vue d'ensemble", icon: LayoutDashboard },
      { path: "/espace-admin/conversion", label: "Conversion", icon: TrendingUp },
    ],
  },
  {
    title: "Gestion",
    items: [
      { path: "/espace-admin/users", label: "Utilisateurs", icon: Users },
      { path: "/espace-admin/tirages", label: "Tirages", icon: Dices },
      { path: "/espace-admin/messages", label: "Messages", icon: Inbox },
      { path: "/espace-admin/promo-campaigns", label: "Campagnes promo", icon: Gift },
      { path: "/espace-admin/announcements", label: "Annonces NL", icon: Megaphone },
    ],
  },
  {
    title: "Logs",
    items: [
      { path: "/espace-admin/activities", label: "Activité", icon: Activity },
      { path: "/espace-admin/login-attempts", label: "Connexions", icon: Shield },
      { path: "/espace-admin/notification-logs", label: "Emails", icon: Mail },
      { path: "/espace-admin/sondages", label: "Sondages", icon: MessageSquare },
      { path: "/espace-admin/email-preview", label: "Preview emails", icon: Eye },
      { path: "/espace-admin/logs", label: "Logs applicatifs", icon: FileText },
    ],
  },
  {
    title: "Navigation",
    items: [
      { path: "/", label: "Retour au site", icon: ArrowLeft, external: true },
    ],
  },
];

interface AdminSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export const AdminSidebar = ({ collapsed, onToggle }: AdminSidebarProps) => {
  const location = useLocation();

  const isActive = (path: string) => {
    if (path === "/espace-admin" && location.pathname === "/espace-admin") {
      return true;
    }
    if (path !== "/espace-admin" && location.pathname.startsWith(path)) {
      return true;
    }
    return false;
  };

  return (
    <aside className={`${styles.sidebar} ${collapsed ? styles.collapsed : ""}`}>
      <div className={styles.header}>
        <Link to="/espace-admin/" className={styles.logoLink}>
          <Logo size={collapsed ? 24 : 28} />
          {!collapsed && <span className={styles.logoText}>Admin</span>}
        </Link>
        <button
          className={styles.toggleButton}
          onClick={onToggle}
          aria-label={collapsed ? "Développer" : "Réduire"}
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      <nav className={styles.nav}>
        {navSections.map((section) => (
          <div key={section.title} className={styles.section}>
            {!collapsed && (
              <span className={styles.sectionTitle}>{section.title}</span>
            )}
            <ul className={styles.navList}>
              {section.items.map((item) => (
                <li key={item.path}>
                  {item.external ? (
                    <a
                      href={item.path}
                      className={`${styles.navLink} ${isActive(item.path) ? styles.active : ""}`}
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon size={20} className={styles.navIcon} />
                      {!collapsed && (
                        <span className={styles.navLabel}>{item.label}</span>
                      )}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className={`${styles.navLink} ${isActive(item.path) ? styles.active : ""}`}
                      title={collapsed ? item.label : undefined}
                    >
                      <item.icon size={20} className={styles.navIcon} />
                      {!collapsed && (
                        <span className={styles.navLabel}>{item.label}</span>
                      )}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
};
